/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect } from 'react';

import {
    PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_WIDTH_1080P,
} from '../../../config';
import { Settings } from '../../../core/device/Settings';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { Clock } from '../../Clock';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { ScreenHeaderProps } from './ScreenHeaderProps';

import './ScreenHeader.scss';

const ScreenHeader: FunctionComponent<ScreenHeaderProps> = ({
    title,
    titleSuffix,
    clockType,
}): JSX.Element => {
    const titleTextRef = React.useRef<HTMLDivElement>(null);

    const CSS_MAST_TEXT: string = 'mask-text';

    const services = new ServicesClientImpl();
    const primaryBrandingImageUrl = services.getPartnerBrandingImageUrl(
        PRIMARY_BRANDING_LOGO_WIDTH_1080P,
        PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    );
    const settingsService = new Settings();
    const isVideoWindowEnabled = settingsService.isVideoWindowEnabled();

    // Check if the title suffix is a year type suffix (e.g., "(2023)") and set the flag
    // accordingly
    let isNotYearTypeSuffix = true;
    if (titleSuffix && titleSuffix.match(/\(\d+\)/)) {
        isNotYearTypeSuffix = false;
    }
    // Check if the title text overflows its container and apply the 'mask-text' class if it does
    // This is done to prevent the text from overflowing and to apply a mask effect to the
    // overflowing text
    useEffect(() => {
        if (titleTextRef.current) {
            const titleTextElement = titleTextRef.current;
            const textWidth = titleTextElement.scrollWidth;
            const containerWidth = titleTextElement.clientWidth;

            if (textWidth > containerWidth) {
                titleTextElement.classList.add(CSS_MAST_TEXT);
            } else {
                titleTextElement.classList.remove(CSS_MAST_TEXT);
            }
        }
    }, [title]);

    return (
        <div className={`screen-header-container ${isVideoWindowEnabled ? 'adjust-width' : ''}`}>
            <div className="left-content">
                <ImageLoader
                    src={primaryBrandingImageUrl}
                    imageType={ImageLoaderTypes.SCREEN_HEADER_PRIMARY_BRANDING}
                />
                <div className="divider" />
                <div className="title-container">
                    <div className="title-text" ref={titleTextRef}>
                        {title}
                        {titleSuffix && isNotYearTypeSuffix ? ' -' : null}
                    </div>
                    {titleSuffix ? <div className="title-suffix-text">{titleSuffix}</div> : null}
                </div>
            </div>

            <div className="right-content">{clockType && <Clock type={clockType} />}</div>
        </div>
    );
};

export default ScreenHeader;
